import { SidebarToggle } from './SidebarToggle';
import { useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { token } from '@atlaskit/tokens';
import { sidebarTemplates, Template } from '../constants/templates';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { useDocumentStorage } from '../providers/document-storage';
import { useEditorActions } from '../providers/actions-provider';
import { LoseChangesModal } from './LoseChangesModal';
import { ContextPanelConsumer } from '@atlaskit/editor-common/ui';
import { PopupPreview } from './PopupPreview';
import { TemplatesList } from './TemplatesList';
import { getAnalyticsWebClient } from '../utils/analytics';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { SelectedType } from '@atlaskit/tabs/types';
import { TipCard } from './TipCard';
import { tips } from '../constants/tips';
import { wacTemplates } from '../constants/wac-templates';
import { isVersion } from 'src/utils/version';

export const ANIM_SPEED_MS = 500;
export const akEditorSwoopCubicBezier = `cubic-bezier(0.15, 1, 0.3, 1)`;
const EXPANDED_WIDTH = 320;
const COLLAPSED_WIDTH = 72;
const PREVIEW_REOPEN_DELAY = 250;
const templates = sidebarTemplates.map((template) => wacTemplates[template]);

export const panel = (expanded: boolean) => css`
  width: ${expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH}px;
  height: 100%;
  transition: width ${ANIM_SPEED_MS}ms ${akEditorSwoopCubicBezier};
  overflow: hidden;
  box-shadow: inset 2px 0 0 0 ${token('color.border')};
`;

export const content = (expanded: boolean) => css`
  box-sizing: border-box;
  padding: ${expanded ? token('space.200', '16px') : '50px'}
    ${token('space.200', '16px')} 0px ${expanded ? '36px' : ''};
  width: ${expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH}px;
  height: 100%;
  overflow-y: auto;
`;

const headingContainer = (expanded: boolean) => css`
  display: flex;
  justify-content: ${expanded ? 'space-between' : 'right'};
  align-items: center;
  padding-left: ${expanded ? '8px' : '0'};
  padding-right: ${expanded ? '0' : '4px'};
`;

const templatesDisclosure = css`
  font-size: 14px;
  line-height: 16px;
  color: ${token('color.text')};
  padding: 24px 16px;
  background-color: ${token('color.background.information')};
  margin-bottom: 16px;
`;

export const templatePanel = css`
  display: flex;
  flex-direction: column;
`;

export const tipsPanel = css`
  display: flex;
  flex-direction: column;
`;

export interface TemplatesSidebarProps {
  onTemplateChange: (template: Template) => void;
}

export const TemplatesSidebar = ({
  onTemplateChange
}: TemplatesSidebarProps) => {
  const [expanded, setExpanded] = useState(!isVersion(2));
  const [modalOpen, setModalOpen] = useState(false);
  const [previewEnabled, setPreviewEnabled] = useState(true);
  const { title } = useDocumentStorage();
  const actions = useEditorActions();
  const selectedTemplate = useRef<Template | null>(null);
  const broadcastWidthRef = useRef<((width: number) => void) | null>(null);
  const analyticsClient = getAnalyticsWebClient();

  const [selected, setSelected] = useState(0);

  const handleUpdate = useCallback(
    (index: SelectedType) => {
      const actionSubject =
        index === 0 ? 'selectTemplatesTab' : 'selectTipsTab';
      analyticsClient.sendUIEvent({
        action: 'clicked',
        actionSubject,
        source: 'confluenceSandbox'
      });
      setSelected(index);
    },
    [setSelected]
  );

  const selectTemplate = (template: Template) => {
    selectedTemplate.current = template;
    setPreviewEnabled(false);
    if (hasModified()) {
      setModalOpen(true);
      return;
    } else {
      analyticsClient.sendUIEvent({
        action: 'clicked',
        actionSubject: 'selectTemplateButton',
        source: 'confluenceSandbox',
        attributes: {
          templateId: template.urlSlug,
          templateName: template.name
        }
      });
      yesChangeTemplate();
    }
  };

  const yesChangeTemplate = () => {
    setTimeout(() => {
      setPreviewEnabled(true);
    }, PREVIEW_REOPEN_DELAY);

    if (!selectedTemplate.current) {
      return;
    }

    onTemplateChange(selectedTemplate.current);
    setExpanded(false);
  };

  const hasModified = () => !!title || !actions.isDocumentEmpty();

  useEffect(() => {
    const contextPanelWidth = expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH;
    if (broadcastWidthRef.current) {
      broadcastWidthRef.current(contextPanelWidth);
    }
  }, [expanded]);

  const TemplatePanel = () => (
    <div css={templatePanel}>
      <PopupPreview
        templates={templates}
        enabled={!modalOpen && previewEnabled}
      >
        {({ showPreview, hidePreview }) => (
          <TemplatesList
            templates={templates}
            expanded={expanded}
            onTemplateMouseEnter={showPreview}
            onTemplateListMouseLeave={hidePreview}
            onTemplateSelect={(template) => {
              selectTemplate(template);
            }}
          />
        )}
      </PopupPreview>
      {expanded && (
        <div css={templatesDisclosure}>
          {'Try hundreds more free templates when you sign up for Confluence.'}
        </div>
      )}
    </div>
  );

  return (
    <ContextPanelConsumer>
      {({ broadcastWidth, broadcastPosition }) => {
        broadcastWidthRef.current = broadcastWidth;
        broadcastPosition(false);

        return (
          <div
            css={panel(expanded)}
            data-testid="context-panel-panel"
            aria-labelledby="context-panel-title"
            role="dialog"
          >
            <div data-testid="context-panel-content" css={content(expanded)}>
              <div css={headingContainer(expanded)}>
                <SidebarToggle
                  expanded={expanded}
                  onToggle={() => {
                    setExpanded(!expanded);
                    setSelected(0);
                  }}
                />
              </div>
              {expanded ? (
                <Tabs
                  onChange={handleUpdate}
                  selected={selected}
                  id="controlled"
                >
                  <TabList>
                    <Tab>{'Templates'}</Tab>
                    <Tab>{'Tips'}</Tab>
                  </TabList>

                  <TabPanel>
                    <TemplatePanel />
                  </TabPanel>
                  <TabPanel>
                    <div css={tipsPanel}>
                      {tips.map((tip) => {
                        return <TipCard {...tip} key={tip.key} />;
                      })}
                    </div>
                  </TabPanel>
                </Tabs>
              ) : (
                <TemplatePanel />
              )}
            </div>

            <ModalTransition>
              {modalOpen && (
                <LoseChangesModal
                  onClose={(accept) => {
                    if (accept) {
                      analyticsClient.sendUIEvent({
                        action: 'clicked',
                        actionSubject: 'replaceTemplateButton',
                        source: 'confluenceSandbox',
                        attributes: selectedTemplate.current
                          ? {
                              templateId: selectedTemplate.current.urlSlug,
                              templateName: selectedTemplate.current.name
                            }
                          : undefined
                      });
                      yesChangeTemplate();
                    }
                    selectedTemplate.current = null;
                    setModalOpen(false);
                  }}
                />
              )}
            </ModalTransition>
          </div>
        );
      }}
    </ContextPanelConsumer>
  );
};
